import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Skeleton } from 'antd';
import { AppstoreOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { clientServicesApi } from '../../../api/clientServicesApi';
import { useBookingFlowStore } from '../../../store/bookingFlowStore';
import { useMainButton } from '../../../hooks/useMainButton';
import { ErrorRetry } from '../ui/ErrorRetry';
import { EmptyState } from '../ui/EmptyState';
import type { ClientService } from '../../../types/client';

interface Props {
  onNext: () => void;
}

function ServiceCardSkeleton() {
  return (
    <div style={{ padding: '0 16px 16px' }}>
      <Skeleton.Input
        active
        style={{
          width: 150,
          height: 20,
          borderRadius: 'var(--bl-radius-sm)',
          marginTop: 14,
          marginBottom: 14,
          display: 'block',
        }}
      />
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            marginBottom: 10,
            borderRadius: 'var(--bl-radius-lg)',
            border: '2px solid transparent',
            background: 'var(--bl-surface)',
            minHeight: 56,
          }}
        >
          <Skeleton.Avatar
            active
            size={44}
            shape="square"
            style={{ borderRadius: 'var(--bl-radius)', flexShrink: 0 }}
          />
          <div style={{ flex: 1 }}>
            <Skeleton.Input
              active
              style={{
                width: '65%',
                height: 14,
                marginBottom: 6,
                display: 'block',
                borderRadius: 'var(--bl-radius-sm)',
              }}
            />
            <Skeleton.Input
              active
              style={{
                width: '45%',
                height: 12,
                display: 'block',
                borderRadius: 'var(--bl-radius-sm)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export const ServiceStep: React.FC<Props> = ({ onNext }) => {
  const { t, i18n } = useTranslation('customer');
  const providerId = useBookingFlowStore((s) => s.providerId);
  const serviceId = useBookingFlowStore((s) => s.serviceId);
  const setService = useBookingFlowStore((s) => s.setService);

  const [services, setServices] = useState<ClientService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchServices = useCallback(async () => {
    if (providerId === null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await clientServicesApi.getList(providerId);
      setServices(data.filter((svc) => svc.active));
    } catch {
      setError(t('bookingFlow.service.loadError'));
    } finally {
      setLoading(false);
    }
  }, [providerId, t]);

  useEffect(() => { void fetchServices(); }, [fetchServices]);

  // Refetch when interface language changes — backend returns translated `name`.
  useEffect(() => {
    const handler = () => { void fetchServices(); };
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, [i18n, fetchServices]);

  useMainButton(t('bookingFlow.nextBtn'), onNext, serviceId !== null);

  if (providerId === null || loading) return <ServiceCardSkeleton />;

  if (error !== null) {
    return <ErrorRetry message={error} onRetry={() => void fetchServices()} />;
  }

  if (services.length === 0) {
    return <EmptyState title={t('bookingFlow.service.empty')} />;
  }

  return (
    <div style={{ padding: '0 16px 16px' }}>
      <Typography.Text
        style={{
          display: 'block',
          fontSize: 17,
          fontWeight: 600,
          color: 'var(--bl-text)',
          padding: '14px 0 10px',
        }}
      >
        {t('bookingFlow.service.title')}
      </Typography.Text>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {services.map((svc) => {
          const isSelected = serviceId === svc.id;
          return (
            <button
              key={svc.id}
              type="button"
              onClick={() => setService(svc.id, svc.confirmationModel)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 14px',
                minHeight: 56,
                borderRadius: 'var(--bl-radius-lg)',
                background: isSelected ? 'rgba(31,139,76,0.10)' : 'var(--bl-surface)',
                border: isSelected
                  ? '2px solid rgba(31,139,76,1)'
                  : '2px solid var(--bl-divider)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                WebkitTapHighlightColor: 'transparent',
                width: '100%',
                transition: 'background 0.15s ease-out, border-color 0.15s ease-out',
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 'var(--bl-radius)',
                  flexShrink: 0,
                  background: isSelected ? 'rgba(31,139,76,1)' : 'rgba(31,139,76,0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  color: isSelected ? '#fff' : 'rgba(31,139,76,1)',
                  marginTop: 2,
                  transition: 'background 0.15s ease-out',
                }}
              >
                <AppstoreOutlined />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography.Text
                  style={{
                    display: 'block',
                    fontSize: 15,
                    fontWeight: 600,
                    color: isSelected ? 'rgba(31,139,76,1)' : 'var(--bl-text)',
                  }}
                >
                  {svc.name}
                </Typography.Text>

                {svc.confirmationModel === 'APPROVAL_REQUIRED' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'rgba(217,119,6,1)',
                      background: 'rgba(217,119,6,0.10)',
                      borderRadius: 20,
                      padding: '2px 8px',
                      marginTop: 3,
                    }}
                  >
                    {t('bookingFlow.service.approvalRequired')}
                  </span>
                )}

                {svc.description !== null && svc.description !== '' && (
                  <Typography.Text
                    style={{
                      display: '-webkit-box' as React.CSSProperties['display'],
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
                      overflow: 'hidden',
                      fontSize: 13,
                      color: 'var(--bl-text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    {svc.description}
                  </Typography.Text>
                )}

                <Typography.Text
                  style={{
                    display: 'block',
                    fontSize: 13,
                    color: 'var(--bl-text-secondary)',
                    marginTop: 2,
                  }}
                >
                  {t('bookingFlow.service.durationPrice', {
                    duration: svc.durationMinutes,
                    price: svc.price.toLocaleString('uz-UZ'),
                  })}
                </Typography.Text>
              </div>

              {/* Check */}
              {isSelected && (
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: 'rgba(31,139,76,1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    color: '#fff',
                    alignSelf: 'flex-start',
                    marginTop: 12,
                  }}
                >
                  <CheckOutlined />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
